import { AnalyticsBrowser } from '@segment/analytics-next';
import * as Sentry from '@sentry/nextjs';
import { uuidv4 } from '@soundxyz/utils';
import type { EventNames, EventProperties } from '@soundxyz/utils/analytics';
import { uaParser } from '@utils/constants';
import { getAnalyticsPageCategoryFromRoute } from '@utils/helpers';

const UNIQUE_ANONYMOUS_ID_FALLBACK = uuidv4();
const SEGMENT_WRITE_KEY = process.env.NEXT_PUBLIC_SEGMENT_WRITE_KEY;
const SEGMENT_PROXY_URL = process.env.NEXT_PUBLIC_SEGMENT_PROXY_URL;

if (!SEGMENT_WRITE_KEY) {
  throw Error('Segment write key not found! Is ENV `NEXT_PUBLIC_SEGMENT_WRITE_KEY` defined?');
}

if (!SEGMENT_PROXY_URL) {
  throw Error('Segment proxy URL not found! Is ENV `NEXT_PUBLIC_SEGMENT_PROXY_URL` defined?');
}

export const AnalyticsClient = AnalyticsBrowser.load({
  writeKey: SEGMENT_WRITE_KEY,
  cdnURL: SEGMENT_PROXY_URL,
});

export function getAnonymousID() {
  if (!AnalyticsClient.instance) {
    return UNIQUE_ANONYMOUS_ID_FALLBACK;
  }
  return String(AnalyticsClient.instance.user().anonymousId());
}

export const sendPageAnalytics = async ({
  url,
  initialLand,
}: {
  url: string;
  initialLand: boolean;
}) => {
  const analyticsPageCategory = getAnalyticsPageCategoryFromRoute({ url, initialLand });

  if (analyticsPageCategory) {
    const result = uaParser.getResult();
    const isbot = (await import('isbot')).default;

    if (isbot(result.ua)) {
      // Do not report page view analytics from bots
      return;
    }

    let referrer = document.referrer;
    if (url.includes('?email=') || url.includes('&email=')) {
      // Get Customer.io campaign ID from URL
      const parsedUrl = new URL('https://sound.xyz' + url);
      const parsedParams = new URLSearchParams(parsedUrl.search);
      const campaignId = parsedParams.get('email');

      if (Number(campaignId) >= 0 && Number(campaignId) <= 1000) {
        referrer = `https://fly.customer.io/workspaces/137203/journeys/composer/transactional/${campaignId}`;
      }
    }

    AnalyticsClient.page(analyticsPageCategory, undefined, {
      height: window?.innerHeight || 0,
      width: window?.innerWidth || 0,
      version: process.env.NEXT_PUBLIC_WEBAPP_VERSION,
      platform: 'WEB',
      browser: {
        name: result.browser.name,
        version: result.browser.version,
      },
      device: {
        vendor: result.device.vendor,
        name: result.os.name,
        version: result.os.version,
      },
      referrer,
    });
  } else {
    Sentry.captureMessage(
      `${url} format does not match any analytics page categories. Page Visit is not registered to Segment.`,
      {
        level: 'warning',
      },
    );
  }
};

export async function trackClickEvent<
  Association extends EventNames,
  Event extends keyof EventProperties<Association>,
>({ event, properties }: { event: Event; properties: EventProperties<Event>[Association] }) {
  try {
    const result = uaParser.getResult();
    const trackEvent = await AnalyticsClient.track(event, {
      height: window?.innerHeight || 0,
      width: window?.innerWidth || 0,
      version: process.env.NEXT_PUBLIC_WEBAPP_VERSION,
      platform: 'WEB',
      browser: {
        name: result.browser.name,
        version: result.browser.version,
      },
      device: {
        vendor: result.device.vendor,
        name: result.os.name,
        version: result.os.version,
      },
      ...properties,
    });
    return trackEvent;
  } catch (e) {
    Sentry.captureException(e, {
      level: 'warning',
    });
    return null;
  }
}
